import { mdiPencil } from "@mdi/js";
import { Button, Input, ModalProps, Row } from "antd";
import { CreateUpdateRoleDto } from "api/generated/lumen";
import { useDispatch, useSelector } from "app/store";
import { MaterialIcon } from "components/MaterialIcon";
import { Permission } from "components/Permission";
import CustomForm from "components/form/CustomForm";
import CustomModal from "components/ui/CustomModal";
import Claims from "features/auth/claims";
import useEditFormModal from "hooks/useEditModal";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { GrapeAntdForm, requiredField, useGrapeAntdForm } from "widgets/form";
import { ListRequestParams } from "widgets/table/useTableUtils";

export interface RoleCreateModalProps extends ModalProps {
  fetchDataParams?: ListRequestParams;
}

const RoleCreateModal: FC<RoleCreateModalProps> = ({
  visible,
  fetchDataParams,
  onCancel,
}) => {
  const { t } = useTranslation();
  const formUtils = useGrapeAntdForm<CreateUpdateRoleDto>();
  const dispatch = useDispatch();
  const { roles, editingRoleId } = useSelector(
    (state) => state.rolesListSettings
  );
  const { createRole, editRole } = useSelector(
    (state) => state.loading.effects.rolesListSettings
  );

  const getInitialValues = useCallback(
    (itemData: CreateUpdateRoleDto) => ({
      name: itemData.name,
      description: itemData.description,
    }),
    []
  );

  const { formMode, setFormMode, itemBeingEdited } = useEditFormModal<any, any>(
    {
      formUtils,
      getInitialValues,
      modalIsVisible: visible,
      resetItemId: dispatch.rolesListSettings.resetEditingRoleData,
      itemId: editingRoleId,
      list: roles,
    }
  );

  const handleFinish = useCallback(
    async (values: CreateUpdateRoleDto): Promise<void> => {
      if (formMode === "Create") {
        await dispatch.rolesListSettings.createRole(values);
      }

      if (formMode === "Edit") {
        await dispatch.rolesListSettings.editRole({
          id: editingRoleId,
          values,
        });
      }

      dispatch.rolesListSettings.fetchRoles(fetchDataParams);
      formUtils.form.resetFields();
      onCancel?.("" as any);
    },
    [
      dispatch.rolesListSettings,
      fetchDataParams,
      formMode,
      editingRoleId,
      formUtils.form,
      onCancel,
    ]
  );

  return (
    <CustomModal
      visible={visible}
      onCancel={onCancel}
      title={
        formMode === "View"
          ? t("settings.roles.modalTitle")
          : formMode === "Edit"
          ? t("settings.roles.editModalTitle")
          : t("settings.roles.createModalTitle")
      }
      size="default"
      onOk={formUtils.form.submit}
      destroyOnClose
      okButtonProps={{
        loading: createRole.loading || editRole.loading,
        disabled: formMode === "View",
      }}
      cancelButtonProps={{ disabled: createRole.loading || editRole.loading }}
      formInstance={formUtils.form}
    >
      <CustomForm
        formUtils={formUtils}
        onFinish={handleFinish}
        viewMode={formMode === "View"}
      >
        {formMode === "View" && (
          <Permission acceptableClaims={[Claims.Role.Write]}>
            <Row justify="end">
              <Button
                icon={<MaterialIcon path={mdiPencil} />}
                onClick={() => setFormMode("Edit")}
              >
                {t("common.edit")}
              </Button>
            </Row>
          </Permission>
        )}

        <GrapeAntdForm.Item
          name="name"
          label={t("settings.roles.name")}
          rules={[requiredField(t)]}
        >
          <Input size="large" disabled={itemBeingEdited?.isBuiltIn} />
        </GrapeAntdForm.Item>

        <GrapeAntdForm.Item
          name="description"
          label={t("settings.roles.description")}
        >
          <Input size="large" />
        </GrapeAntdForm.Item>
      </CustomForm>
    </CustomModal>
  );
};

export default RoleCreateModal;
